import React from 'react'
import { get, uniqBy, flatten, noop } from 'lodash'
import { IBooking, IEmployee, Role } from '@api'
import { isAdmin, isBookingManager } from '@utils/auth'
import { useUser } from '@hooks'
import { NewGridTable, IGridTableColumn } from '@ui'
import track from '@track'
import Event from '@track/Event'
import { UseBookingsTableHook, BaseTableFilters } from './types'

type CurrentBookingsTableProps<BookingHookT> = {
  showFilters?: boolean
  headerTopWithoutFilter?: number
  bookingHook: BookingHookT
  columns: IGridTableColumn<IBooking>[]
}

function CurrentBookingsTable<
  TFilter extends BaseTableFilters,
  BookingHookT extends UseBookingsTableHook<TFilter> = UseBookingsTableHook<TFilter>
>({
  bookingHook,
  columns,
  showFilters = false,
  headerTopWithoutFilter = 70,
}: CurrentBookingsTableProps<BookingHookT>) {
  const { user } = useUser()

  const admin = user?.employee?.role && isAdmin(user?.employee?.role)
  const bookingManager = user?.employee?.role && isBookingManager(user?.employee?.role)
  const locations = admin
    ? []
    : user?.employee.locationAdmins.map(
        ({
          floorPlan: {
            id: floorId,
            building: { id },
          },
        }) => `${floorId}:${id}`
      ) || []
  const {
    manage_all,
    managed_departments = [],
    managed_employees = [],
  } = user?.employee?.user?.bookingManagerSetting || {}
  const allEmployeesFromDepartment = managed_departments.map(
    ({ employeeConnection: { edges } }: { employeeConnection: { edges: { node: IEmployee }[] } }) =>
      edges.map(({ node }) => node)
  )

  const allManagableEmployees = (
    uniqBy(
      [
        ...flatten(allEmployeesFromDepartment),
        ...managed_employees,
        { id: user?.employee.id }, // add self for Booking manager
      ],
      'id'
    ) as IEmployee[]
  ).map((e) => e.id)

  const getBookingData = () => {
    return bookingHook.items.map((item, i, ar) => {
      const floor = get(item, item.data.calendar ? 'data.calendar.space.floor' : 'data.space.floor')
      const floorId = get(floor, 'id')
      const building = get(floor, 'building')
      const isEmployeManageable = !(
        allManagableEmployees && allManagableEmployees.includes(ar[i]?.data?.employee?.id)
      )

      const otherRoles = () => {
        switch (user?.employee.role) {
          case Role.SPACE_CAPTAIN:
            return locations?.includes(`${floorId}:${building?.id}`) && isDepartmentManageable()

          case Role.LOCATION_ADMIN:
            return locations?.includes(`${floorId}:${building?.id}`)

          case Role.DEPARTMENT_ADMIN:
            return isDepartmentManageable()

          default:
            return true
        }
      }

      const isDepartmentManageable = () => {
        return user?.employee?.departmentAdmins
          ?.map(({ department: { id } }) => id)
          .includes(get(item, 'data.employee.department.id', ''))
      }
      return {
        ...ar[i],
        data: {
          ...ar[i].data,
          disabled: bookingManager ? (manage_all ? false : isEmployeManageable) : !otherRoles(),
        },
      }
    })
  }

  return (
    <NewGridTable<IBooking>
      status={bookingHook.status}
      columns={columns}
      data={admin ? bookingHook.items : getBookingData()}
      selection={bookingHook.selection}
      page={{
        hasNextPage: bookingHook.pageInfo?.hasNextPage || false,
        endCursor: bookingHook.pageInfo?.endCursor || '',
        onNextPage: bookingHook?.nextPage || noop,
      }}
      sort={{
        column: bookingHook.sort.column,
        order: bookingHook.sort.order,
        onSort: (column: any) => {
          bookingHook.selection?.clear()
          bookingHook.sortBy(column)
          track.bookings({
            eventName: Event.SORT,
            props: { columnName: column },
          })
        },
      }}
      headerTop={showFilters ? 210 : headerTopWithoutFilter}
    />
  )
}

export default CurrentBookingsTable
